import React, { useState, useEffect } from 'react';
import { Task, PaginatedResponse } from '../../types';
import { convertIDECostToCredits, calculateWebCredits } from '../../config/costs';
import { ChartBarIcon, XMarkIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { format } from 'date-fns';
import UserTasksSkeleton from './UserTasksSkeleton';

interface UserTasksProps {
  userId: number;
  onClose: () => void;
  fetchTasks: (userId: number, params: any) => Promise<any>;
}

type TaskType = 'ide' | 'web' | 'devops' | 'security';

const UserTasks: React.FC<UserTasksProps> = ({ userId, onClose, fetchTasks }) => {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [taskType, setTaskType] = useState<TaskType>('ide');

  const loadTasks = async (page: number = 1, searchQuery: string = '') => {
    setLoading(true);
    try {
      const response = await fetchTasks(userId, {
        page,
        limit: 10,
        task_name: searchQuery || undefined,
        task_type: taskType
      });
      
      if (response.success && response.data) {
        setTasks(response.data);
        if (response.meta) {
          setTotalPages(response.meta.pages);
          setTotalItems(response.meta.total);
        }
      }
    } catch (error) {
      console.error('Error loading tasks:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTasks(currentPage, search);
  }, [userId, currentPage, taskType]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    loadTasks(1, search);
  };

  const formatDate = (timestamp: number | string | null) => {
    if (!timestamp) return 'N/A';
    return format(new Date(timestamp), 'MMM d, yyyy HH:mm');
  };

  return (
    <div className="fixed inset-0 bg-black/80 dark:bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="relative w-full max-w-7xl bg-white dark:bg-black border border-gray-200 dark:border-[#1F2937] rounded-xl shadow-lg max-h-[90vh] flex flex-col">
        <div className="sticky top-0 z-10 flex items-center justify-between p-6 border-b border-gray-200 dark:border-[#1F2937] bg-white dark:bg-black">
          <div className="flex items-center gap-4">
            <ChartBarIcon className="h-6 w-6 text-primary-500" />
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">User Tasks</h2>
            <div className="flex items-center gap-2 ml-4">
              <button
                onClick={() => setTaskType('ide')}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors duration-200 ${
                  taskType === 'ide'
                    ? 'bg-primary-500 text-white'
                    : 'bg-gray-100 dark:bg-[#1F2937] text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-[#1F2937]/80'
                }`}
              >
                IDE Tasks
              </button>
              <button
                onClick={() => setTaskType('web')}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors duration-200 ${
                  taskType === 'web'
                    ? 'bg-primary-500 text-white'
                    : 'bg-gray-100 dark:bg-[#1F2937] text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-[#1F2937]/80'
                }`}
              >
                Web Tasks
              </button>
              <button
                onClick={() => setTaskType('devops')}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors duration-200 ${
                  taskType === 'devops'
                    ? 'bg-primary-500 text-white'
                    : 'bg-gray-100 dark:bg-[#1F2937] text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-[#1F2937]/80'
                }`}
              >
                DevOps Tasks
              </button>
              <button
                onClick={() => setTaskType('security')}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors duration-200 ${
                  taskType === 'security'
                    ? 'bg-primary-500 text-white'
                    : 'bg-gray-100 dark:bg-[#1F2937] text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-[#1F2937]/80'
                }`}
              >
                Security Tasks
              </button>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors duration-200"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <div className="p-6 flex-1 overflow-hidden flex flex-col">
          {/* Search Bar */}
          <form onSubmit={handleSearch} className="mb-6 flex-shrink-0">
            <div className="relative">
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search tasks..."
                className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-gray-200 dark:border-[#1F2937] bg-white dark:bg-black text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors duration-200"
              />
              <MagnifyingGlassIcon className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
            </div>
          </form>

          {/* Tasks Table */}
          {loading ? (
            <UserTasksSkeleton />
          ) : (
            <div className="flex-1 overflow-hidden flex flex-col">
              <div className="overflow-x-auto flex-1">
                <table className="w-full">
                  <thead className="sticky top-0 bg-white dark:bg-black z-10">
                    <tr className="text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      {taskType === 'ide' ? (
                        <>
                          <th className="px-6 py-2.5 w-[200px]">Task Name</th>
                          <th className="px-6 py-2.5 w-[500px]">Task</th>
                          <th className="px-6 py-2.5 w-[100px]">Credits</th>
                          <th className="px-6 py-2.5 w-[150px]">Timestamp</th>
                        </>
                      ) : taskType === 'web' ? (
                        <>
                          <th className="px-6 py-2.5 w-[200px]">Project ID</th>
                          <th className="px-6 py-2.5 w-[500px]">Name</th>
                          <th className="px-6 py-2.5 w-[100px]">Credits</th>
                          <th className="px-6 py-2.5 w-[150px]">Created At</th>
                        </>
                      ) : taskType === 'devops' ? (
                        <>
                          <th className="px-6 py-2.5 w-[200px]">Operation</th>
                          <th className="px-6 py-2.5 w-[500px]">Description</th>
                          <th className="px-6 py-2.5 w-[100px]">Credits</th>
                          <th className="px-6 py-2.5 w-[150px]">Timestamp</th>
                        </>
                      ) : (
                        <>
                          <th className="px-6 py-2.5 w-[200px]">Scan Type</th>
                          <th className="px-6 py-2.5 w-[500px]">Target</th>
                          <th className="px-6 py-2.5 w-[100px]">Credits</th>
                          <th className="px-6 py-2.5 w-[150px]">Timestamp</th>
                        </>
                      )}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-[#1F2937]">
                    {tasks && tasks.map((task: any) => (
                      <tr key={task.id || task.project_id || task.operation_id} className="hover:bg-gray-50 dark:hover:bg-[#1F2937]/10 transition-colors duration-200">
                        {taskType === 'ide' ? (
                          <>
                            <td className="px-6 py-2.5 text-sm text-gray-900 dark:text-white">
                              {task.task_name}
                            </td>
                            <td className="px-6 py-2.5 text-sm text-gray-500 dark:text-gray-400">
                              <div className="max-h-32 overflow-y-auto custom-scrollbar">
                                <pre className="whitespace-pre-wrap font-mono text-xs">
                                  {task.task}
                                </pre>
                              </div>
                            </td>
                            <td className="px-6 py-2.5 text-sm text-gray-500 dark:text-gray-400">
                              {convertIDECostToCredits(task.total_cost || 0).toFixed(2)}
                            </td>
                            <td className="px-6 py-2.5 text-sm text-gray-500 dark:text-gray-400">
                              {formatDate(task.timestamp)}
                            </td>
                          </>
                        ) : taskType === 'web' ? (
                          <>
                            <td className="px-6 py-2.5 text-sm text-gray-900 dark:text-white">
                              {task.project_id}
                            </td>
                            <td className="px-6 py-2.5 text-sm text-gray-500 dark:text-gray-400">
                              {task.name}
                            </td>
                            <td className="px-6 py-2.5 text-sm text-gray-500 dark:text-gray-400">
                              {calculateWebCredits(task.input_tokens || 0, task.output_tokens || 0).toFixed(2)}
                            </td>
                            <td className="px-6 py-2.5 text-sm text-gray-500 dark:text-gray-400">
                              {formatDate(task.created_at)}
                            </td>
                          </>
                        ) : taskType === 'devops' ? (
                          <>
                            <td className="px-6 py-2.5 text-sm text-gray-900 dark:text-white">
                              {task.operation_type || task.task_name || 'DevOps Operation'}
                            </td>
                            <td className="px-6 py-2.5 text-sm text-gray-500 dark:text-gray-400">
                              <div className="max-h-32 overflow-y-auto custom-scrollbar">
                                {task.description || task.task || 'DevOps task execution'}
                              </div>
                            </td>
                            <td className="px-6 py-2.5 text-sm text-gray-500 dark:text-gray-400">
                              {(task.credits_used || task.total_cost || 1).toFixed(2)}
                            </td>
                            <td className="px-6 py-2.5 text-sm text-gray-500 dark:text-gray-400">
                              {formatDate(task.timestamp || task.created_at)}
                            </td>
                          </>
                        ) : (
                          <>
                            <td className="px-6 py-2.5 text-sm text-gray-900 dark:text-white">
                              {task.scan_type || task.task_name || 'Security Scan'}
                            </td>
                            <td className="px-6 py-2.5 text-sm text-gray-500 dark:text-gray-400">
                              <div className="max-h-32 overflow-y-auto custom-scrollbar">
                                {task.target || task.description || task.task || 'Security analysis'}
                              </div>
                            </td>
                            <td className="px-6 py-2.5 text-sm text-gray-500 dark:text-gray-400">
                              {(task.credits_used || task.total_cost || 1).toFixed(2)}
                            </td>
                            <td className="px-6 py-2.5 text-sm text-gray-500 dark:text-gray-400">
                              {formatDate(task.timestamp || task.created_at)}
                            </td>
                          </>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="mt-4 flex items-center justify-between flex-shrink-0">
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  Showing {tasks?.length || 0} of {totalItems} results
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-1 rounded-md bg-white dark:bg-black border border-gray-200 dark:border-[#1F2937] text-sm text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-[#1F2937]/10 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1 rounded-md bg-white dark:bg-black border border-gray-200 dark:border-[#1F2937] text-sm text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-[#1F2937]/10 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserTasks;
